import { useTranslation } from 'react-i18next';
import { ActionIcon, Box, Group, Stack, Text, ThemeIcon, Tooltip } from '@mantine/core';
import { IconCircleCheck, IconDownload, IconFileText } from '@tabler/icons-react';
import SignatureBadge from '@/components/app/deals/SignatureBadge';
import { useDownloadDocument } from '@/hooks/useDeals';
import { signatureStateView } from '@/lib/dealStatus';
import { fmtFwdDate } from '@/lib/forwarding';
import type { BargainStepsInfo, SignatureRole } from '@/types/deals';

interface UpdTorgStepProps {
  readonly info: BargainStepsInfo;
}

// Шаг отображает посуточные УПД (закрывающие документы по дням отгрузки). Универсальные типы
// УПД/ТОРГ-12 удалены из контракта order-service как неиспользуемые — в наборе оставлены только
// реально существующие типы.
const UPD_TYPES = new Set(['DAILY_UPD_TEMPLATE', 'SIGNED_DAILY_UPD']);
const SIGN_ROLES: readonly SignatureRole[] = ['AGENT', 'BUYER', 'SELLER'];

// Шаг «УПД и ТОРГ-12» (id 7, условный). Закрывающие документы по дням отгрузки подписываются в ЭДО
// (агент → покупатель встречной подписью). Read-only отображение + статус.
export default function UpdTorgStep({ info }: UpdTorgStepProps) {
  const { t } = useTranslation();
  const ps = info.processingStep ?? '';
  const isAgent = info.userRole === 'AGENT';
  const download = useDownloadDocument();

  const docs = (info.documents ?? []).filter((d) => d.documentType && UPD_TYPES.has(d.documentType));

  if (ps === 'UPD_TORG12_SIGNED') {
    return (
      <Group gap="xs" wrap="nowrap">
        <ThemeIcon size={32} radius="xl" variant="light" color="lime">
          <IconCircleCheck size={18} />
        </ThemeIcon>
        <Text fz="sm" fw={500}>
          {t('app.deals.updTorg.signed')}
        </Text>
      </Group>
    );
  }

  return (
    <Stack gap="md">
      <Text fz="sm" c="gray.8">
        {t(isAgent ? 'app.deals.updTorg.agentSign' : 'app.deals.updTorg.waiting')}
      </Text>

      {docs.length === 0 ? (
        <Text fz="sm" c="gray.6">
          {t('app.deals.updTorg.generating')}
        </Text>
      ) : (
        <Stack gap="sm">
          {docs.map((doc) => {
            const roles = SIGN_ROLES.filter((role) => signatureStateView(doc.signatures?.[role]?.state) !== null);
            return (
              <Group key={doc.id} justify="space-between" wrap="nowrap" align="flex-start" gap="sm">
                <Group gap="sm" wrap="nowrap" align="flex-start" style={{ minWidth: 0 }}>
                  <ThemeIcon size={32} radius="md" variant="light" color="gray">
                    <IconFileText size={18} />
                  </ThemeIcon>
                  <Box style={{ minWidth: 0 }}>
                    <Text fz="sm" fw={500} lineClamp={1}>
                      {doc.name ?? t('app.deals.docs.untitled')}
                    </Text>
                    {doc.deliveryDate ? (
                      <Text fz="xs" c="gray.6">
                        {fmtFwdDate(doc.deliveryDate)}
                      </Text>
                    ) : null}
                    {roles.length > 0 ? (
                      <Group gap={4} wrap="wrap" mt={4}>
                        {roles.map((role) => (
                          <SignatureBadge key={role} role={role} info={doc.signatures?.[role]} />
                        ))}
                      </Group>
                    ) : null}
                  </Box>
                </Group>
                {doc.id != null ? (
                  <Tooltip label={t('app.deals.docs.download')}>
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      aria-label={t('app.deals.docs.download')}
                      loading={download.isPending && download.variables === doc.id}
                      onClick={() => download.mutate(doc.id!)}
                    >
                      <IconDownload size={18} />
                    </ActionIcon>
                  </Tooltip>
                ) : null}
              </Group>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
}
